import { QRCodeSVG } from 'qrcode.react';
import { RefreshCw, Clock, Phone, Shield } from "lucide-react";

// Public origin for the QR codes embedded in this card. We deliberately
// do NOT use ``BACKEND_URL`` (= REACT_APP_BACKEND_URL) here because the
// build-time env var may point at an internal hosting domain on certain
// production deployments — a bug surfaced by Fabrizio 23/02/2026 when
// an iPhone scan revealed a truncated internal hostname in the QR
// payload in front of the LoJack customer.
//
// ``window.location.origin`` always resolves to whatever the customer
// is currently browsing (``securewallet.lojack.eu`` in prod, the
// internal preview hostname during development). The Kubernetes
// ingress routes ``/api`` to the backend on both environments, so the
// URL embedded in the QR is functionally equivalent to
// ``${BACKEND_URL}/api/...`` minus the whitelabel leak.
const _qrPublicOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  return (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
};

// Mini LoJack-branded pass preview — replaces the generic Smartphone
// icon used as placeholder (req. A. Zorzutti Stand-up 04/06/2026 pt. 17,
// Fase 5: "loghi brand wallet Apple/Google"). Customer sees a glimpse
// of the actual pass before downloading instead of a generic phone icon.
const PassPreviewThumbnail = ({ operationCenter, customer }) => {
  const phone = operationCenter?.phone || customer?.operation_center?.phone || '+39 02 36 58 9333';
  return (
    <div className="relative mx-auto mb-4 w-full max-w-[200px] aspect-[1.6/1] rounded-xl overflow-hidden bg-gradient-to-br from-[#0A0A0A] via-[#141414] to-[#0A0A0A] border border-gray-700/60 shadow-lg" data-testid="wallet-pass-thumbnail">
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #E31837 30%, #E31837 70%, transparent 100%)' }} />
      <div className="absolute inset-0 flex flex-col justify-between p-3">
        <div className="flex items-start justify-between">
          <img src="/lojack_logo_white.png" alt="LoJack" className="h-5" style={{ filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.3))' }} />
          <Shield className="w-3 h-3 text-lojack-red opacity-60" strokeWidth={1.5} />
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full border border-lojack-red/40 flex items-center justify-center bg-lojack-red/10 flex-shrink-0">
            <Phone className="w-2.5 h-2.5 text-lojack-red" strokeWidth={2} />
          </div>
          <span className="text-[10px] text-lojack-red font-bold tracking-wider truncate">{phone}</span>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, #E31837 30%, #E31837 70%, transparent 100%)' }} />
    </div>
  );
};

// ─── Brand icons ───────────────────────────────────────────────────────

const AppleWalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const GoogleWalletIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
  </svg>
);

// Detect iOS so we can prioritize Apple Wallet visually (and surface the
// "Aggiungi al Wallet" button on phones where it actually works inline).
const isIOSDevice = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPhone / iPad / iPod, and modern iPadOS (which reports as Mac + touch).
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && navigator.maxTouchPoints > 1);
};


// ─── Per-device localized scan hints ─────────────────────────────────

const _scanHint = (country) => ({
  IT: "Scansiona con il tuo telefono Android",
  FR: "Scannez avec votre telephone Android",
  ES: "Escanea con tu telefono Android",
  DE: "Mit Ihrem Android-Telefon scannen",
  PL: "Zeskanuj telefonem z Androidem",
  UK: "Scan with your Android phone",
})[country] || "Scan with your Android phone";

const _scanHintIOS = (country) => ({
  IT: "Scansiona con il tuo iPhone",
  FR: "Scannez avec votre iPhone",
  ES: "Escanea con tu iPhone",
  DE: "Mit Ihrem iPhone scannen",
  PL: "Zeskanuj swoim iPhonem",
  UK: "Scan with your iPhone",
})[country] || "Scan with your iPhone";

// Heuristic: hide the inline QR if the user is already on the target
// device (e.g. an iPhone user doesn't need an iPhone-scanned QR — they
// just tap the button). Helps keep the layout compact on mobile.
const _isDesktop = () => {
  if (typeof window === "undefined") return true;
  try {
    if (window.matchMedia?.("(max-width: 768px)")?.matches) return false;
    if ("ontouchstart" in window) return false;
    if (/iPad|iPhone|iPod|Android/i.test(navigator.userAgent || "")) return false;
  } catch (_) { /* SSR safety */ }
  return true;
};


// ─── Card components ──────────────────────────────────────────────────

const AppleWalletCard = ({ translations, addToWallet, appleWalletLoading, applePassReady, operationCenter, customer, customerId, uiLanguage }) => {
  // 3 visual states:
  //   - applePassReady === null  → probing (still allow click but show spinner via loading)
  //   - applePassReady === true  → green CTA
  //   - applePassReady === false → "In configurazione" yellow notice, button disabled
  const probing = applePassReady === null;
  const ready = applePassReady === true;
  const disabled = appleWalletLoading || (!probing && !ready);
  // Show the iPhone-scan QR only when the Apple pass is actually ready
  // (so the customer never gets a QR pointing to a 4xx endpoint), AND
  // only on desktop browsers (an iPhone visitor doesn't need to scan
  // themselves — they just tap the button). Fabrizio req. 11/06/2026.
  const showQr = ready && customerId && _isDesktop();

  return (
    <div className="wallet-card text-center" data-testid="apple-wallet-card">
      <PassPreviewThumbnail operationCenter={operationCenter} customer={customer} />
      <h4 className="text-lg font-semibold text-white mb-2">Apple Wallet</h4>
      <p className="text-gray-400 text-sm mb-6">
        {translations.apple_desc}
      </p>
      <button
        onClick={() => addToWallet('apple')}
        disabled={disabled}
        className="wallet-btn-apple w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="add-apple-wallet-btn"
      >
        {appleWalletLoading ? (
          <><RefreshCw className="w-5 h-5 animate-spin" /> {_generatingPass((uiLanguage || customer?.country || 'IT').toUpperCase())}</>
        ) : (
          <><AppleWalletIcon /> {translations.add_to_apple}</>
        )}
      </button>

      {!ready && !probing && (
        <div
          className="mt-4 px-3 py-2 rounded-lg bg-lojack-clause-500/10 border border-lojack-clause-500/30 text-lojack-clause-300 text-xs flex items-center justify-center gap-2"
          data-testid="apple-wallet-not-ready"
        >
          <Clock className="w-3.5 h-3.5" />
          <span>
            {({
              IT: "Apple Wallet in configurazione · disponibile a breve",
              UK: "Apple Wallet being configured · available soon",
              FR: "Apple Wallet en configuration · bientôt disponible",
              ES: "Apple Wallet en configuración · pronto disponible",
              DE: "Apple Wallet wird konfiguriert · bald verfügbar",
              PL: "Apple Wallet w konfiguracji · wkrótce dostępny",
            })[(uiLanguage || customer?.country || 'IT').toUpperCase()]
              || "Apple Wallet being configured · available soon"}
          </span>
        </div>
      )}

      {showQr && (
        <div className="mt-6 pt-6 border-t border-gray-700" data-testid="apple-wallet-qr">
          <p className="text-gray-500 text-xs mb-3 uppercase tracking-wider">
            {_scanHintIOS(uiLanguage || customer?.country)}
          </p>
          <div className="inline-block bg-white p-3 rounded-xl">
            <QRCodeSVG
              value={`${_qrPublicOrigin()}/api/wallet/${customerId}/apple-pass`}
              size={180}
              level="M"
              bgColor="#ffffff"
              fgColor="#111111"
            />
          </div>
        </div>
      )}
    </div>
  );
};


const _generatingPass = (country) => ({
  IT: "Generazione pass...",
  UK: "Generating pass...",
  FR: "Génération du pass...",
  ES: "Generando pass...",
  DE: "Pass wird erstellt...",
  PL: "Generowanie pass...",
})[country] || "Generating pass...";

const GoogleWalletCard = ({ translations, addToWallet, googleWalletLoading, googleWalletUrl, customerId, customer, operationCenter, uiLanguage }) => {
  // Hide the QR on mobile/Android — the customer is already on the
  // device that should receive the pass, scanning themselves makes no
  // sense and just adds visual clutter. Desktop visitors keep it so
  // they can install the card on their phone via the camera.
  //
  // NOTE: the QR points to the static `/api/wallet/{id}/google-redirect`
  // endpoint (server-side 302 to the JWT save URL), so it does NOT
  // depend on `googleWalletUrl` being pre-fetched. We previously gated
  // on `googleWalletUrl &&` as a vestige of an older implementation —
  // that made the QR disappear until the auto-fetch completed (or the
  // user clicked the button). Fix req. Fabrizio 23/06/2026 — make the
  // Google QR symmetric to the Apple QR (always visible on desktop).
  const showQr = customerId && _isDesktop();
  return (
  <div className="wallet-card text-center" data-testid="google-wallet-card">
    <PassPreviewThumbnail operationCenter={operationCenter} customer={customer} />
    <h4 className="text-lg font-semibold text-white mb-2">Google Wallet</h4>
    <p className="text-gray-400 text-sm mb-6">
      {translations.google_desc}
    </p>
    <button
      onClick={() => addToWallet('google')}
      disabled={googleWalletLoading}
      className="wallet-btn-google w-full justify-center"
      data-testid="add-google-wallet-btn"
    >
      {googleWalletLoading ? (
        <><RefreshCw className="w-5 h-5 animate-spin" /> {_generatingPass((uiLanguage || customer?.country || 'IT').toUpperCase())}</>
      ) : (
        <><GoogleWalletIcon /> {translations.add_to_google}</>
      )}
    </button>

    {showQr && (
      <div className="mt-6 pt-6 border-t border-gray-700" data-testid="google-wallet-qr">
        <p className="text-gray-500 text-xs mb-3 uppercase tracking-wider">
          {_scanHint(uiLanguage || customer?.country)}
        </p>
        <div className="inline-block bg-white p-3 rounded-xl">
          <QRCodeSVG
            value={`${_qrPublicOrigin()}/api/wallet/${customerId}/google-redirect`}
            size={180}
            level="M"
            bgColor="#ffffff"
            fgColor="#111111"
          />
        </div>
      </div>
    )}
  </div>
  );
};


// ─── Public component ─────────────────────────────────────────────────

export const WalletButtons = ({
  translations,
  addToWallet,
  googleWalletLoading,
  googleWalletUrl,
  appleWalletLoading = false,
  applePassReady,
  customerId,
  customer,
  operationCenter,
  // UI language (req. Fabrizio 10/06/2026) — overrides customer.country
  // for the localized "Scan with your phone" hint. PDFs untouched.
  uiLanguage,
}) => {
  // On iOS we want Apple Wallet shown first (left on desktop / top on
  // mobile). On every other platform, Google goes first.
  const iosFirst = isIOSDevice();

  return (
    <div className="grid md:grid-cols-2 gap-6 mb-8" data-testid="wallet-buttons-grid">
      {iosFirst ? (
        <>
          <AppleWalletCard
            translations={translations}
            addToWallet={addToWallet}
            appleWalletLoading={appleWalletLoading}
            applePassReady={applePassReady}
            operationCenter={operationCenter}
            customer={customer}
            customerId={customerId}
            uiLanguage={uiLanguage}
          />
          <GoogleWalletCard
            translations={translations}
            addToWallet={addToWallet}
            googleWalletLoading={googleWalletLoading}
            googleWalletUrl={googleWalletUrl}
            customerId={customerId}
            customer={customer}
            operationCenter={operationCenter}
            uiLanguage={uiLanguage}
          />
        </>
      ) : (
        <>
          <GoogleWalletCard
            translations={translations}
            addToWallet={addToWallet}
            googleWalletLoading={googleWalletLoading}
            googleWalletUrl={googleWalletUrl}
            customerId={customerId}
            customer={customer}
            operationCenter={operationCenter}
            uiLanguage={uiLanguage}
          />
          <AppleWalletCard
            translations={translations}
            addToWallet={addToWallet}
            appleWalletLoading={appleWalletLoading}
            applePassReady={applePassReady}
            operationCenter={operationCenter}
            customer={customer}
            customerId={customerId}
            uiLanguage={uiLanguage}
          />
        </>
      )}
    </div>
  );
};
